import { AudioListener, PositionalAudio } from 'three/webgpu'
import { types } from '@theatre/core'
import PointerTilt from './PointerTilt'
import staticPath from '../utils/staticPath'

export default class SceneAudio extends PointerTilt {
	static defaults = {
		url: staticPath('audio/site_audio.aac'),
		offset: 8,
		refDistance: 3,
		volume: 1,
		open: 20000,
		muffle: { frequency: 320, resonance: 4, volume: 0.35, ramp: 0.25 },
	}

	constructor(camera, options = {}) {
		super(options)

		this.options = { ...SceneAudio.defaults, ...this.options }

		this.listener = this.#listener(camera)
		this.muffled = false

		this.audio = new PositionalAudio(this.listener)
		this.audio.position.z = this.options.offset
		this.audio.setRefDistance(this.options.refDistance)
		this.audio.setVolume(this.options.volume)
		this.add(this.audio)

		this.filter = this.audio.context.createBiquadFilter()
		this.filter.type = 'lowpass'
		this.filter.frequency.value = this.options.open
		this.filter.Q.value = 0.0001
		this.audio.setFilter(this.filter)

		this.element = new Audio(this.options.url)
		this.element.loop = true
		this.element.crossOrigin = 'anonymous'
		this.audio.setMediaElementSource(this.element)

		this._onClick = event => event.target.closest('[data-audio-toggle]') && this.toggle()
		this._onVisibility = () => this.#visibility()

		document.addEventListener('click', this._onClick)
		document.addEventListener('visibilitychange', this._onVisibility)
	}

	#visibility() {
		if (document.hidden) {
			this.resume = !this.element.paused
			this.element.pause()
		} else if (this.resume) {
			this.element.play()
		}
	}

	#listener(camera) {
		const existing = camera.children.find(child => child.type === 'AudioListener')

		if (existing) return existing

		const listener = new AudioListener()
		camera.add(listener)

		return listener
	}

	// Muffled keeps the track running under a lowpass instead of stopping it, so the
	// filter sweep reads as going underwater rather than a cut.
	#apply() {
		const { open, volume, muffle } = this.options
		const time = this.audio.context.currentTime

		this.filter.frequency.setTargetAtTime(this.muffled ? muffle.frequency : open, time, muffle.ramp)
		this.filter.Q.setTargetAtTime(this.muffled ? muffle.resonance : 0.0001, time, muffle.ramp)
		this.audio.gain.gain.setTargetAtTime(this.muffled ? volume * muffle.volume : volume, time, muffle.ramp)
	}

	toggle() {
		this.audio.context.resume()

		// First gesture starts playback; after that the button only swaps muffle state.
		if (this.element.paused) {
			this.element.play()
			this.muffled = false
		} else {
			this.muffled = !this.muffled
		}

		this.#apply()

		for (const el of document.querySelectorAll('[data-audio-toggle]')) {
			el.dataset.audioPlaying = !this.element.paused && !this.muffled
		}
	}

	bindStudio(sheet) {
		if (!sheet) return

		const { open, muffle } = this.options

		// muffled here so the knobs below are audible while dragging; the button
		// writes this.muffled directly, so the panel checkbox can read stale.
		this.control = sheet.object('Audio / Muffle', {
			muffled: types.boolean(false),
			frequency: types.number(muffle.frequency, { range: [40, 20000] }),
			resonance: types.number(muffle.resonance, { range: [0.0001, 20] }),
			volume: types.number(muffle.volume, { range: [0, 1] }),
			ramp: types.number(muffle.ramp, { range: [0.01, 2] }),
			open: types.number(open, { range: [200, 20000] }),
		})

		this.unbind = this.control.onValuesChange(({ open, muffled, ...values }) => {
			this.options.open = open
			this.muffled = muffled
			Object.assign(muffle, values)
			this.#apply()
		})
	}

	dispose() {
		super.dispose()
		this.unbind?.()
		document.removeEventListener('click', this._onClick)
		document.removeEventListener('visibilitychange', this._onVisibility)
		this.element.pause()
		this.element.src = ''
	}
}
